/**
 * Query Provider
 *
 * Configures and provides TanStack React Query.
 */

import { type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { env } from '@shared/config';
import { DataverseError } from '@shared/api';

// =============================================================================
// Query Client Configuration
// =============================================================================

/**
 * Create query client with appropriate defaults
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Stale time: 5 minutes
        staleTime: 5 * 60 * 1000,
        // Cache time: 30 minutes
        gcTime: 30 * 60 * 1000,
        // Retry configuration
        retry: (failureCount, error) => {
          // Don't retry on 4xx client errors (except 429 rate-limit, which is transient)
          if (error instanceof DataverseError) {
            if (error.status >= 400 && error.status < 500 && error.status !== 429) {
              return false;
            }
          }
          // Retry up to 3 times for server errors, 429, and network errors
          return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Refetch on window focus in production
        refetchOnWindowFocus: env.isProduction,
        // Don't refetch on mount if data is fresh
        refetchOnMount: true,
      },
      mutations: {
        // Retry once on failure
        retry: 1,
        retryDelay: 1000,
      },
    },
  });
}

// Create singleton client
const queryClient = createQueryClient();

// =============================================================================
// Provider Component
// =============================================================================

interface QueryProviderProps {
  children: ReactNode;
}

/**
 * Query Provider component
 * Provides React Query context and optional DevTools
 */
export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}

// Export for direct access if needed
export { queryClient };
